import { Call } from "@twilio/voice-sdk";
import { useTranslation } from "react-i18next";
import { usePhoneNumberLabel } from "../../../hooks";
import { extractPhoneFromClientId, formatInitials, formatName, formatPhone } from "@tellescope/utils";
import { useSecureFileUrl, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { Avatar, AvatarFallback, AvatarImage, Button, Tooltip, TooltipContent, TooltipTrigger } from "@tellescope/ui";
import { Phone, PhoneOff } from "lucide-react";

export interface IncomingCallItemProps {
  call: Call;
  onAnswer: (call: Call) => void;
  onReject: (call: Call) => void;
}

/**
 * A single incoming call item that fetches the enduser data for avatar and name display.
 * Extracted as a component so hooks can be used per call.
 */
export const IncomingCallItem = ({ call, onAnswer, onReject }: IncomingCallItemProps) => {
  const { t } = useTranslation("calling");
  const getPhoneLabel = usePhoneNumberLabel();

  const enduserId = call.customParameters?.get("eId") ?? null;
  const enduserNameParam = call.customParameters?.get("eName") ?? null;
  const phone = call.parameters?.From ?? "Unknown";
  const to = call.parameters?.To ?? null;
  const orgNumber = to ? extractPhoneFromClientId(to) : null;
  const orgLabel = orgNumber ? getPhoneLabel(orgNumber) : null;

  // Fetch enduser by ID (regular inbound calls)
  const enduserByIdQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: { id: enduserId ?? "" },
    enabled: !!enduserId,
  });

  // Fallback: fetch enduser by phone number (queue calls without eId)
  const enduserByPhoneQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: { filter: { phone }, limit: 1 },
    enabled: !enduserId && phone !== "Unknown",
    select: (data) => data.pages.flat(),
  });

  const enduser = enduserByIdQuery.data ?? enduserByPhoneQuery.data?.[0] ?? null;
  const resolvedEnduserId = enduser?.id ?? enduserId;
  const avatarUrl = useSecureFileUrl(enduser?.avatar);

  // Resolve display name: prefer fetched enduser name, then Twilio param, then phone
  const displayName = (enduser ? formatName(enduser) : null) || enduserNameParam || phone;
  const showPhone = displayName !== phone;

  // Avatar rendering
  const avatarElement = resolvedEnduserId ? (
    <Avatar className="h-10 w-10">
      {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
      <AvatarFallback useStaticBackground deterministicColorMode="dark" id={resolvedEnduserId}>
        {(enduser ? formatInitials(enduser).toUpperCase() : null) ||
          (enduserNameParam ? formatInitials(enduserNameParam).toUpperCase() : null) ||
          "?"}
      </AvatarFallback>
    </Avatar>
  ) : (
    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
      <Phone className="h-5 w-5 text-primary animate-bounce" />
    </div>
  );

  return (
    <div className="p-4 animate-pulse-subtle">
      {/* Caller info */}
      <div className="flex items-center gap-3 mb-3">
        {avatarElement}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{displayName}</div>
          {showPhone && <div className="text-sm text-muted-foreground truncate">{formatPhone(phone)}</div>}
          <div className="text-xs text-green-600 font-medium">{t("Incoming call...")}</div>
          {orgLabel && (
            <div className="text-xs text-muted-foreground">
              {t("on")}{" "}
              {orgLabel !== orgNumber ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help border-b border-dotted border-muted-foreground/50">{orgLabel}</span>
                  </TooltipTrigger>
                  <TooltipContent>{formatPhone(orgNumber)}</TooltipContent>
                </Tooltip>
              ) : (
                formatPhone(orgNumber)
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={() => onReject(call)}
          className="flex-1 h-12 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          data-trackingid="incoming-call-decline"
        >
          <PhoneOff className="h-5 w-5 mr-2" />
          {t("Decline")}
        </Button>
        <Button
          variant="default"
          size="lg"
          onClick={() => onAnswer(call)}
          className="flex-1 h-12 bg-green-600 hover:bg-green-700"
          data-trackingid="incoming-call-answer"
        >
          <Phone className="h-5 w-5 mr-2" />
          {t("Answer")}
        </Button>
      </div>
    </div>
  );
};
